import { ContractStatus } from "@boark/shared/types/contract";
import { ContractWithCounterParty } from "@boark/shared/types/contract-with-counter-party";
import { useMemo } from "react";

import { ContractCostStackedBarChart } from "../../components/contract-cost-stacked-bar-chart";
import { H1 } from "../../components/h1";
import { useGetContracts } from "../../queries/contract";
import { useLocale } from "../../stores/locale";
import { annualizedContractCost } from "../../utils/contract-annual-cost";
import { formatPrice } from "../../utils/format-price";
import { recordIdToId } from "../../utils/record-id-to-id";

type Row = { contract: ContractWithCounterParty; annual: number };

export function ContractSummary() {
  const { locale: t, currentLanguage } = useLocale();
  const { data: contracts } = useGetContracts();

  const formatAmount = (n: number) => formatPrice(n, currentLanguage, "SEK");

  const activeRows = useMemo((): Row[] => {
    if (!contracts?.length) {
      return [];
    }
    return contracts
      .filter((c) => c.status === ContractStatus.ACTIVE)
      .map((contract) => ({
        contract,
        annual: annualizedContractCost(contract),
      }))
      .sort(
        (a, b) =>
          b.annual - a.annual ||
          a.contract.title.localeCompare(b.contract.title),
      );
  }, [contracts]);

  const barData = useMemo(() => {
    const withCost = activeRows.filter((r) => r.annual > 0);
    const tagTotals = new Map<string, number>();
    for (const { contract, annual } of withCost) {
      const tags = [
        ...new Set(contract.tags.map((t) => t.trim()).filter(Boolean)),
      ];
      for (const tag of tags) {
        tagTotals.set(tag, (tagTotals.get(tag) ?? 0) + annual);
      }
    }

    const tags = [...tagTotals.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([tag]) => tag);

    const contracts = withCost.map(({ contract }) => contract.title);

    const byTagByContract = withCost.map(({ contract, annual }) => {
      const set = new Set(contract.tags.map((t) => t.trim()).filter(Boolean));
      return tags.map((tag) => (set.has(tag) ? annual : 0));
    });

    const tagByContractDatasets = withCost.map(({ contract }, i) => ({
      label: truncateTitle(contract.title, 36),
      values: byTagByContract[i] ?? [],
    }));

    const contractTotalsDataset = [
      {
        label: "Annual cost",
        values: withCost.map((row) => row.annual),
        colors: withCost.map((_row, i) => {
          const hue = Math.round((360 / Math.max(withCost.length, 1)) * i);
          return `hsl(${hue} 58% 52%)`;
        }),
      },
    ];

    return {
      tags,
      contracts: contracts.map((c) => truncateTitle(c, 36)),
      tagByContractDatasets,
      contractTotalsDataset,
    };
  }, [activeRows]);

  const totalAnnualCost = useMemo(
    () => activeRows.reduce((sum, row) => sum + row.annual, 0),
    [activeRows],
  );

  return (
    <div className="w-full min-h-full flex flex-col gap-10 pb-8">
      <H1>{t.contract.summaryTitle}</H1>
      <p className="text-sm text-gray-600 max-w-3xl">
        {t.contract.summaryIntro}
      </p>

      <section className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold">
          {t.contract.summaryActiveList}
        </h2>
        <div className="text-sm text-gray-700 font-medium">
          {t.contract.total}: {formatAmount(totalAnnualCost)} /{" "}
          {t.contract.summaryPerYear}
        </div>
        {activeRows.length === 0 ? (
          <p className="text-gray-500">{t.contract.summaryNoActive}</p>
        ) : (
          <ul className="border border-gray-200 rounded-xs divide-y divide-gray-200 max-w-3xl">
            {activeRows.map(({ contract, annual }) => (
              <li
                key={recordIdToId(contract.id)}
                className="px-3 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1"
              >
                <span className="font-medium">{contract.title}</span>
                <span className="text-sm text-gray-600 tabular-nums">
                  {annual > 0 ? (
                    <>
                      {formatAmount(annual)}
                      <span className="text-gray-400">
                        {" "}
                        / {t.contract.summaryPerYear}
                      </span>
                    </>
                  ) : (
                    t.contract.summaryNoCost
                  )}
                  {contract.tags.length > 0 && (
                    <span className="block sm:inline sm:ml-2 text-gray-500">
                      · {contract.tags.join(", ")}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold">
          {t.contract.summaryTagBarsTitle}
        </h2>
        <p className="text-sm text-gray-600 max-w-3xl">
          {t.contract.summaryTagBarsExplain}
        </p>
        {activeRows.length === 0 ? (
          <p className="text-gray-500">{t.contract.summaryNoActive}</p>
        ) : barData.tags.length === 0 ? (
          <p className="text-gray-500">{t.contract.summaryTagBarsNoTags}</p>
        ) : (
          <ContractCostStackedBarChart
            datasets={barData.tagByContractDatasets}
            tooltipFormat={formatAmount}
            xLabels={barData.tags}
          />
        )}
      </section>

      <section className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold">
          {t.contract.summaryContractBarsTitle}
        </h2>
        <p className="text-sm text-gray-600 max-w-3xl">
          {t.contract.summaryContractBarsExplain}
        </p>
        {activeRows.length === 0 ? (
          <p className="text-gray-500">{t.contract.summaryNoActive}</p>
        ) : barData.contracts.length === 0 ? (
          <p className="text-gray-500">{t.contract.summaryPieEmpty}</p>
        ) : (
          <ContractCostStackedBarChart
            datasets={barData.contractTotalsDataset}
            stacked={false}
            tooltipFormat={formatAmount}
            xLabels={barData.contracts}
          />
        )}
      </section>
    </div>
  );
}

function truncateTitle(title: string, max: number): string {
  const t = title.trim();
  if (t.length <= max) {
    return t;
  }
  return `${t.slice(0, max - 1)}…`;
}
